import { observer } from 'mobx-react';

import Heading from '@/common/components/Heading/Heading';
import Loader from '@/common/components/Loader/Loader';
import Modal, { CENTER, ModalProps } from '@/common/components/Modal/Modal';
import ConfirmationFactory from '@/common/services/ConfirmationFactory';
import { AUTH_DEFAULT_DESCRIPTION, AUTH_DEFAULT_TITLE } from '@/user/components/AuthModal/AuthModal.constants';
import AuthService from '@/user/services/AuthService';

import styles from './AuthModal.module.css';
import AuthModalTitle from './AuthModalTitle/AuthModalTitle';
import { useAuthModal } from './useAuthModal';

type AuthModalResult = 'success' | 'close';

export interface AuthModalConfirmationProps {
  title?: Nullable<string>;
  description?: Nullable<string>;
  preventReloadOnAuth?: boolean;
  onAfterClose?: () => void;
}

export interface AuthModalProps extends Pick<ModalProps, 'isShowed' | 'onClose'>, AuthModalConfirmationProps {
  onAfterAuth: () => void;
}

function AuthModal(props: AuthModalProps) {
  const { isShowed, title = AUTH_DEFAULT_TITLE, description = AUTH_DEFAULT_DESCRIPTION } = props;

  const { isLoading, onAfterClose } = useAuthModal(props);

  return (
    <Modal isShowed={isShowed} onClose={onAfterClose} position={CENTER} title={<AuthModalTitle title={title} />}>
      <div className={styles.authForm} aria-label="Auth modal form">
        {description && (
          <div className={styles.description}>
            <Heading appearance="h4" className={styles.subtitle}>
              Why?
            </Heading>

            <p>{description}</p>
          </div>
        )}

        <div className={styles.frameContainer}>
          {isLoading && <Loader className={styles.loader} />}

          <iframe className={styles.frame} src={AuthService.generateAuthUrl()} title="Auth frame" />
        </div>
      </div>
    </Modal>
  );
}

export function AuthModalConfirmation(props: AuthModalConfirmationProps = {}) {
  if (!AuthService.canAuthInFrame()) {
    return AuthService.redirectToLoginPage();
  }

  return ConfirmationFactory.generate<AuthModalResult>(({ show, proceed }) => (
    <AuthModal isShowed={show} onAfterAuth={() => proceed('success')} onClose={() => proceed('close')} {...props} />
  ));
}

export default observer(AuthModal);
